import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils';

const Dialog = ({ children, open, onOpenChange, ...props }: any) => {
  const dialogRef = React.useRef<HTMLDialogElement>(null);

  React.useEffect(() => {
    if (open) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [open]);

  const handleClose = () => {
    onOpenChange?.(false);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === dialogRef.current) {
      handleClose();
    }
  };

  return React.cloneElement(children, {
    ...props,
    open,
    onOpenChange,
    dialogRef,
    onClose: handleClose,
    onBackdropClick: handleBackdropClick
  });
};

const DialogTrigger = ({ children, onClick, ...props }: any) => {
  return React.cloneElement(children, {
    ...props,
    onClick: (e: any) => {
      onClick?.(e);
      props.onClick?.(e);
    }
  });
};

const DialogPortal = ({ children, ...props }: any) => children;

const DialogClose = ({ children, onClick, ...props }: any) => {
  return React.cloneElement(children, {
    ...props,
    onClick: (e: any) => {
      onClick?.(e);
      props.onClick?.(e);
    }
  });
};

const DialogOverlay = ({ className, ...props }: any) => null;

const DialogContent = React.forwardRef<HTMLDialogElement, any>(({ className, children, dialogRef, onClose, onBackdropClick, ...props }, ref) => (
  <dialog
    ref={dialogRef || ref}
    onClick={onBackdropClick}
    className={cn(
      'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 open:animate-in open:fade-in-0 open:zoom-in-95 open:slide-in-from-left-1/2 open:slide-in-from-top-[48%] sm:rounded-lg',
      '[&::backdrop]:fixed [&::backdrop]:inset-0 [&::backdrop]:bg-black/50 [&::backdrop]:backdrop-blur-sm',
      className
    )}
    {...props}
  >
    {children}
    <button
      onClick={onClose}
      className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </button>
  </dialog>
));
DialogContent.displayName = 'DialogContent';

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<HTMLHeadingElement, any>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<HTMLParagraphElement, any>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};